import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  BookOpen, Printer, RefreshCw, Filter, Calendar,
  ShoppingCart, FileText, RotateCcw, Truck,
  Pencil, Undo2, Trash2, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JOURNAL_TYPES = [
  { key: 'vouchers', label: 'Voucher Journal', labelAr: 'دفتر اليومية', icon: BookOpen, endpoint: '/reports/journal' },
  { key: 'pos', label: 'POS Sales', labelAr: 'مبيعات نقاط البيع', icon: ShoppingCart, endpoint: '/reports/journal/pos-sales' },
  { key: 'sales', label: 'Sales Invoices', labelAr: 'فواتير المبيعات', icon: FileText, endpoint: '/reports/journal/sales-invoices' },
  { key: 'sales-returns', label: 'Sales Returns', labelAr: 'مرتجع المبيعات', icon: RotateCcw, endpoint: '/reports/journal/sales-returns' },
  { key: 'purchases', label: 'Purchase Invoices', labelAr: 'فواتير المشتريات', icon: Truck, endpoint: '/reports/journal/purchase-invoices' },
  { key: 'purchase-returns', label: 'Purchase Returns', labelAr: 'مرتجع المشتريات', icon: RotateCcw, endpoint: '/reports/journal/purchase-returns' },
];

const fmt = (n) => n ? n.toLocaleString('en-US', { minimumFractionDigits: n % 1 !== 0 ? 3 : 0, maximumFractionDigits: 3 }) : null;
const fmtLBP = (n) => n ? n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : null;
const Dot = () => <span className="text-muted-foreground/40">.</span>;

const JournalPage = () => {
  const { currentOrg, user } = useAuth();
  const [activeTab, setActiveTab] = useState('vouchers');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [showUnbalanced, setShowUnbalanced] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null); // Track which item is being deleted
  
  // POS Sales filters
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Adjustment filter - single formula input
  const [adjFormula, setAdjFormula] = useState('');

  // Fetch suppliers and categories for POS filters
  useEffect(() => {
    if (!currentOrg?.id) return;
    
    const fetchFilters = async () => {
      try {
        const [suppRes, catRes] = await Promise.all([
          axios.get(`${API}/suppliers?organization_id=${currentOrg.id}`),
          axios.get(`${API}/inventory-categories?organization_id=${currentOrg.id}`)
        ]);
        const suppData = Array.isArray(suppRes.data) ? suppRes.data : (suppRes.data.suppliers || []);
        setSuppliers(suppData);
        setCategories(catRes.data || []);
      } catch (err) {
        console.error('Failed to fetch filters:', err);
      }
    };
    
    fetchFilters();
  }, [currentOrg?.id]);

  const loadJournal = useCallback(async () => {
    if (!currentOrg) return;
    setLoading(true);
    setData(null);
    try {
      const type = JOURNAL_TYPES.find(t => t.key === activeTab);
      const params = new URLSearchParams({ organization_id: currentOrg.id });
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      
      // Add POS-specific filters
      if (activeTab === 'pos') {
        if (supplierFilter && supplierFilter !== 'all') {
          params.append('supplier_id', supplierFilter);
        }
        if (categoryFilter && categoryFilter !== 'all') {
          params.append('category_id', categoryFilter);
        }
        // Add adjustment formula filter
        if (adjFormula && adjFormula.trim()) {
          params.append('adj_formula', adjFormula.trim());
        }
      }
      
      const res = await axios.get(`${API}${type.endpoint}?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load journal:', err);
    } finally {
      setLoading(false);
    }
  }, [currentOrg, activeTab, fromDate, toDate, supplierFilter, categoryFilter, adjFormula]);

  // Delete POS transaction handler
  const handleDeletePOSTransaction = async (transactionId, receiptNumber) => {
    const confirmed = window.confirm(
      `⚠️ DELETE POS INVOICE: ${receiptNumber}\n\n` +
      `This will:\n` +
      `• Reverse all accounting entries\n` +
      `• Restore inventory quantities\n` +
      `• Delete linked prescription records\n` +
      `• Delete the voucher\n\n` +
      `This action cannot be undone. Continue?`
    );
    
    if (!confirmed) return;
    
    setDeleteLoading(transactionId);
    try {
      const response = await axios.delete(`${API}/pos/invoices/${transactionId}?restore_inventory=true`);
      alert(
        `✓ Transaction ${receiptNumber} deleted successfully!\n\n` +
        `Details:\n` +
        `• Voucher deleted: ${response.data.details?.voucher_deleted ? 'Yes' : 'No'}\n` +
        `• Inventory restored: ${response.data.details?.inventory_restored ? 'Yes' : 'No'}\n` +
        `• Prescriptions deleted: ${response.data.details?.prescriptions_deleted || 0}`
      );
      // Reload the journal
      loadJournal();
    } catch (err) {
      console.error('Failed to delete transaction:', err);
      alert(`Failed to delete transaction: ${err.response?.data?.detail || err.message}`);
    } finally {
      setDeleteLoading(null);
    }
  };

  const currentType = JOURNAL_TYPES.find(t => t.key === activeTab);
  const Icon = currentType?.icon || BookOpen;

  return (
    <div className="p-4 lg:p-6 space-y-4 max-w-[1400px] mx-auto">
      <Card className="print:shadow-none print:border-none">
        <CardHeader className="pb-3 print:pb-1">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Icon className="w-5 h-5" />
              {currentType?.label} - {currentType?.labelAr}
              <span className="text-xs text-muted-foreground font-normal ml-2">
                {data ? `${activeTab === 'vouchers' ? data.total_vouchers : data.total_count} entries` : ''}
              </span>
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => window.print()} className="print:hidden" data-testid="journal-print-btn">
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Tab selector */}
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setData(null); }} className="print:hidden">
            <TabsList className="flex flex-wrap h-auto gap-1">
              {JOURNAL_TYPES.map(type => (
                <TabsTrigger key={type.key} value={type.key} className="text-xs gap-1" data-testid={`journal-tab-${type.key}`}>
                  <type.icon className="w-3 h-3" />
                  {type.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Date filters */}
          <div className="flex flex-wrap items-end gap-3 print:hidden">
            <div>
              <Label className="text-xs">From:</Label>
              <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-40 h-8" data-testid="journal-from-date" />
            </div>
            <div>
              <Label className="text-xs">To:</Label>
              <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-40 h-8" data-testid="journal-to-date" />
            </div>
            
            {/* POS-specific filters: Supplier and Category */}
            {activeTab === 'pos' && (
              <>
                <div>
                  <Label className="text-xs">Supplier:</Label>
                  <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                    <SelectTrigger className="w-44 h-8" data-testid="journal-supplier-filter">
                      <SelectValue placeholder="All Suppliers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Suppliers</SelectItem>
                      {suppliers.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name || s.company_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Category:</Label>
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="w-44 h-8" data-testid="journal-category-filter">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Adj Filter:</Label>
                  <Input
                    type="text"
                    value={adjFormula}
                    onChange={(e) => setAdjFormula(e.target.value)}
                    placeholder=">0, <-2, =0, <>0"
                    className="w-32 h-8 font-mono text-sm"
                    data-testid="journal-adj-formula"
                    title="Examples: >0, <0, >=5, <=-2, =0, <>0, !=0"
                  />
                </div>
              </>
            )}
            
            <Button onClick={loadJournal} disabled={loading} size="sm" data-testid="load-journal-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Load Journal
            </Button>
            {activeTab === 'vouchers' && (
              <Button variant={showUnbalanced ? 'default' : 'outline'} size="sm" onClick={() => setShowUnbalanced(!showUnbalanced)} data-testid="show-unbalanced-btn">
                <Filter className="w-4 h-4 mr-1" /> Show Unbalanced
              </Button>
            )}
          </div>

          {/* Content */}
          {!data && !loading && (
            <div className="text-center py-12 text-muted-foreground">
              <Icon className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Select a date range and click "Load Journal"</p>
            </div>
          )}

          {loading && (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin text-muted-foreground" />
              <p className="text-muted-foreground">Loading...</p>
            </div>
          )}

          {data && activeTab === 'vouchers' && (
            <VoucherJournal data={data} showUnbalanced={showUnbalanced} />
          )}

          {data && activeTab !== 'vouchers' && (
            <InvoiceJournal 
              data={data} 
              journalType={activeTab} 
              user={user}
              onDeletePOS={handleDeletePOSTransaction}
              deleteLoading={deleteLoading}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};


/* ========== VOUCHER JOURNAL — CARD LAYOUT ========== */
const VoucherJournal = ({ data, showUnbalanced }) => {
  const vouchers = showUnbalanced
    ? data.vouchers.filter(v => !v.is_balanced_usd || !v.is_balanced_lbp)
    : data.vouchers;

  return (
    <div className="space-y-4">
      {vouchers.map(v => {
        const unbalanced = !v.is_balanced_usd || !v.is_balanced_lbp;
        return (
          <div key={v.id} className={`border rounded-lg overflow-hidden ${unbalanced ? 'border-red-400/60' : 'border-border'}`}>
            {/* Card Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-background">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-semibold text-sm text-teal-500 font-mono">{v.voucher_number}</span>
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Calendar className="w-3 h-3" /> {v.date}
                </span>
                <span className="text-xs">{v.description}</span>
                {unbalanced && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-red-100 text-red-600 px-2 py-0.5 rounded">
                    <AlertTriangle className="w-3 h-3" /> UNBALANCED
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 print:hidden">
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" title="Edit">
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" title="Unpost">
                  <Undo2 className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" title="Delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>

            {/* Lines Table */}
            <table className="w-full text-sm">
              <thead>
                <tr className="border-t border-b-2 border-foreground/20">
                  <th className="text-left px-4 py-2 font-semibold text-xs w-[90px]">Account</th>
                  <th className="text-left px-4 py-2 font-semibold text-xs w-[140px]">Name</th>
                  <th className="text-left px-4 py-2 font-semibold text-xs">Description</th>
                  <th className="text-right px-4 py-2 font-semibold text-xs w-[110px]">Debit (XOF)</th>
                  <th className="text-right px-4 py-2 font-semibold text-xs w-[110px]">Credit (XOF)</th>
                  <th className="text-right px-4 py-2 font-semibold text-xs w-[110px]">Debit (EUR)</th>
                  <th className="text-right px-4 py-2 font-semibold text-xs w-[110px]">Credit (EUR)</th>
                </tr>
              </thead>
              <tbody>
                {v.lines.map((line, i) => (
                  <tr key={i} className="border-b border-border/50">
                    <td className="px-4 py-2.5 text-xs font-mono text-muted-foreground">{line.account_code}</td>
                    <td className="px-4 py-2.5 text-xs font-medium">{line.account_name}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{line.description}</td>
                    <td className="px-4 py-2.5 text-right text-xs font-medium text-teal-500">{fmt(line.debit_usd) || <Dot />}</td>
                    <td className="px-4 py-2.5 text-right text-xs font-medium text-teal-500">{fmt(line.credit_usd) || <Dot />}</td>
                    <td className="px-4 py-2.5 text-right text-xs font-medium">{fmtLBP(line.debit_lbp) || <Dot />}</td>
                    <td className="px-4 py-2.5 text-right text-xs font-medium">{fmtLBP(line.credit_lbp) || <Dot />}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-muted/60">
                  <td colSpan={3} className="px-4 py-2 text-xs font-bold">Voucher Total</td>
                  <td className="px-4 py-2 text-right text-xs font-bold text-teal-500">{fmt(v.total_debit_usd)}</td>
                  <td className="px-4 py-2 text-right text-xs font-bold text-teal-500">{fmt(v.total_credit_usd)}</td>
                  <td className="px-4 py-2 text-right text-xs font-bold">{fmtLBP(v.total_debit_lbp)}</td>
                  <td className="px-4 py-2 text-right text-xs font-bold">{fmtLBP(v.total_credit_lbp)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        );
      })}

      {/* Grand Totals */}
      {vouchers.length > 0 && (
        <div className="border-2 border-foreground/30 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <tfoot>
              <tr className="bg-muted">
                <td className="px-4 py-3 font-bold text-sm" style={{ width: 'calc(90px + 140px)' }} colSpan={2}>Grand Total ({vouchers.length} vouchers)</td>
                <td></td>
                <td className="px-4 py-3 text-right font-bold text-sm text-teal-500 w-[110px]">{fmt(data.grand_total.debit_usd)}</td>
                <td className="px-4 py-3 text-right font-bold text-sm text-teal-500 w-[110px]">{fmt(data.grand_total.credit_usd)}</td>
                <td className="px-4 py-3 text-right font-bold text-sm w-[110px]">{fmtLBP(data.grand_total.debit_lbp)}</td>
                <td className="px-4 py-3 text-right font-bold text-sm w-[110px]">{fmtLBP(data.grand_total.credit_lbp)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {vouchers.length === 0 && (
        <p className="text-center text-muted-foreground py-8">No vouchers found for this period.</p>
      )}
    </div>
  );
};


/* ========== INVOICE/POS JOURNAL — CARD LAYOUT ========== */
const InvoiceJournal = ({ data, journalType, user, onDeletePOS, deleteLoading }) => {
  const docs = data.documents || [];
  const isPOS = journalType === 'pos';
  const isPurchase = journalType === 'purchases' || journalType === 'purchase-returns';
  const canDelete = user?.role === 'super_admin' || user?.role === 'admin';

  return (
    <div className="space-y-4">
      {docs.map(doc => {
        const acctName = isPOS
          ? (doc.customer_name || 'Walk-in')
          : isPurchase
            ? (doc.credit_account_name || '-')
            : (doc.debit_account_name || doc.customer_name || '-');
        const acctCode = isPOS ? doc.customer_code : isPurchase ? doc.credit_account_code : doc.debit_account_code;
        const isDeleting = deleteLoading === doc.id;

        return (
          <div key={doc.id} className="border rounded-lg overflow-hidden border-border">
            {/* Card Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-background">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-semibold text-sm text-teal-500 font-mono">{doc.doc_number}</span>
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Calendar className="w-3 h-3" /> {doc.date}
                  {isPOS && doc.time && <span className="ml-1 font-medium">{doc.time}</span>}
                </span>
                {acctCode && <span className="text-xs font-mono text-muted-foreground">{acctCode}</span>}
                <span className="text-xs font-medium">{acctName}</span>
                {isPOS && doc.payment_method && (
                  <span className="text-[10px] bg-muted px-2 py-0.5 rounded">{doc.payment_method}</span>
                )}
                {doc.notes && <span className="text-xs text-muted-foreground italic">{doc.notes}</span>}
              </div>
              <div className="flex items-center gap-2 print:hidden">
                <span className="text-xs text-muted-foreground">{doc.currency}</span>
                {/* Delete button for POS - admin/super_admin only */}
                {isPOS && canDelete && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                    onClick={() => onDeletePOS(doc.id, doc.doc_number)}
                    disabled={isDeleting}
                    title="Delete this POS invoice"
                    data-testid={`delete-pos-${doc.id}`}
                  >
                    {isDeleting ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Lines Table */}
            <table className="w-full text-sm">
              <thead>
                <tr className="border-t border-b-2 border-foreground/20">
                  <th className="text-left px-4 py-2 font-semibold text-xs">Item</th>
                  <th className="text-right px-4 py-2 font-semibold text-xs w-[70px]">Qty</th>
                  <th className="text-left px-4 py-2 font-semibold text-xs w-[70px]">Unit</th>
                  <th className="text-right px-4 py-2 font-semibold text-xs w-[100px]">Unit Price</th>
                  <th className="text-right px-4 py-2 font-semibold text-xs w-[70px]">Disc%</th>
                  <th className="text-right px-4 py-2 font-semibold text-xs w-[110px]">Total (XOF)</th>
                  <th className="text-right px-4 py-2 font-semibold text-xs w-[110px]">Total (EUR)</th>
                  {isPOS && <th className="text-right px-4 py-2 font-semibold text-xs w-[100px]">💰 (XOF)</th>}
                </tr>
              </thead>
              <tbody>
                {doc.lines?.map((line, i) => (
                  <tr key={i} className="border-b border-border/50">
                    <td className="px-4 py-2.5 text-xs font-medium">
                      {line.item_name}
                      {line.item_name_ar ? <span className="text-muted-foreground ml-2">{line.item_name_ar}</span> : ''}
                      {line.free_qty ? <span className="text-green-500 ml-2 text-[10px]">(+{line.free_qty} free)</span> : ''}
                    </td>
                    <td className="px-4 py-2.5 text-right text-xs">{line.quantity}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{line.unit}</td>
                    <td className="px-4 py-2.5 text-right text-xs font-medium text-teal-500">{fmt(line.unit_price) || <Dot />}</td>
                    <td className="px-4 py-2.5 text-right text-xs">{line.discount_percent ? `${line.discount_percent}%` : <Dot />}</td>
                    <td className="px-4 py-2.5 text-right text-xs font-medium text-teal-500">{fmt(line.line_total_usd || line.line_total) || <Dot />}</td>
                    <td className="px-4 py-2.5 text-right text-xs font-medium">{fmtLBP(line.line_total_lbp) || <Dot />}</td>
                    {isPOS && (
                      <td className={`px-4 py-2.5 text-right text-xs font-medium ${(line.profit_usd || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {fmt(line.profit_usd) || <Dot />}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {(doc.discount_amount > 0 || doc.tax_amount > 0) && (
                  <>
                    <tr className="border-t border-border/30">
                      <td colSpan={isPOS ? 6 : 5} className="px-4 py-1.5 text-right text-xs text-muted-foreground">Subtotal</td>
                      <td className="px-4 py-1.5 text-right text-xs">{fmt(doc.subtotal_usd || doc.subtotal || 0)}</td>
                      {isPOS && <td></td>}
                    </tr>
                    {doc.discount_amount > 0 && (
                      <tr>
                        <td colSpan={isPOS ? 6 : 5} className="px-4 py-1 text-right text-xs text-muted-foreground">
                          Discount{doc.discount_percent ? ` (${doc.discount_percent}%)` : ''}
                        </td>
                        <td className="px-4 py-1 text-right text-xs text-red-400">-{fmt(doc.discount_amount)}</td>
                        {isPOS && <td></td>}
                      </tr>
                    )}
                    {doc.tax_amount > 0 && (
                      <tr>
                        <td colSpan={isPOS ? 6 : 5} className="px-4 py-1 text-right text-xs text-muted-foreground">
                          Tax{doc.tax_percent ? ` (${doc.tax_percent}%)` : ''}
                        </td>
                        <td className="px-4 py-1 text-right text-xs">{fmt(doc.tax_amount)}</td>
                        {isPOS && <td></td>}
                      </tr>
                    )}
                  </>
                )}
                <tr className="bg-muted/60">
                  <td colSpan={isPOS ? 6 : 5} className="px-4 py-2 text-xs font-bold">Document Total</td>
                  <td className="px-4 py-2 text-right text-xs font-bold text-teal-500">{fmt(doc.total_usd)}</td>
                  {isPOS ? (
                    <td className={`px-4 py-2 text-right text-xs font-bold ${(doc.profit_usd || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      💰 {fmt(doc.profit_usd || 0)}
                    </td>
                  ) : (
                    <td className="px-4 py-2 text-right text-xs font-bold">{fmtLBP(doc.total_lbp || 0)}</td>
                  )}
                </tr>
                {/* Amount Paid row for POS */}
                {isPOS && (
                  <tr className="bg-emerald-50/50 border-t border-emerald-200/50">
                    <td colSpan={6} className="px-4 py-2 text-xs font-semibold text-emerald-700">
                      Amount Paid
                      <span className="ml-2 font-normal text-emerald-600">
                        ({doc.payment_method === 'cash' ? '💵 Cash' : doc.payment_method === 'card' ? '💳 Card' : doc.payment_method || 'Cash'})
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right text-xs font-bold text-emerald-600">
                      {fmt(doc.payment_amount || doc.total_usd)} {doc.payment_currency || 'XOF'}
                    </td>
                    <td className="px-4 py-2 text-right text-xs font-medium text-emerald-600">
                      {doc.payment_adjustment ? (
                        <span className="text-amber-600" title="Payment adjustment">
                          Adj: {doc.payment_adjustment > 0 ? '-' : '+'}{fmt(Math.abs(doc.payment_adjustment))}
                        </span>
                      ) : (
                        fmtLBP((doc.payment_amount || doc.total_usd) * (doc.payment_exchange_rate || 1)) + ' EUR'
                      )}
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
        );
      })}

      {/* Grand Totals */}
      {docs.length > 0 && (
        <div className="border-2 border-foreground/30 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <tfoot>
              <tr className="bg-muted">
                <td className="px-4 py-3 font-bold text-sm" colSpan={isPOS ? 6 : 5}>Grand Total ({docs.length} documents)</td>
                <td className="px-4 py-3 text-right font-bold text-sm text-teal-500 w-[110px]">{fmt(data.grand_total?.total_usd || 0)}</td>
                {isPOS ? (
                  <td className={`px-4 py-3 text-right font-bold text-sm w-[100px] ${(data.grand_total?.profit_usd || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    💰 {fmt(data.grand_total?.profit_usd || 0)}
                  </td>
                ) : (
                  <td className="px-4 py-3 text-right font-bold text-sm w-[110px]">{fmtLBP(data.grand_total?.total_lbp || 0)}</td>
                )}
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {docs.length === 0 && (
        <p className="text-center text-muted-foreground py-8">No documents found for this period.</p>
      )}
    </div>
  );
};

export default JournalPage;
